/* eslint-disable compat/compat */
/* global cartManager */

/**
 * === Auto gifts (cart) ===========================================================
 *
 * Ajoute/retire automatiquement les variants cadeaux côté PANIER selon les règles
 * dynamiques du metafield shop `zax.auto_gift_rules`, en miroir de la checkout UI
 * extension `ui-auto-gifts`.
 *
 * ⚠️ La logique pure ci-dessous est le PORTAGE de
 *    pdd-shopify-app/extensions/ui-auto-gifts/src/logic.ts
 *    Toute évolution de l'une doit être répercutée sur l'autre (même seuil, mêmes
 *    règles d'exclusion), sinon un cadeau ajouté au panier ne serait pas mis à 100%
 *    au checkout par discount-pdd → le client paierait le cadeau.
 *
 * Réactivité : on écoute l'événement `cart:update` (dispatché par sidecart.js sur
 * document.body à chaque changement de panier) + un run initial à la connexion de
 * l'élément. Pas besoin de recharger la page.
 *
 * Coordination :
 *  - marqueur de ligne : propriété `_zax_auto_gift` (devient un attribut de cart line
 *    au checkout → l'extension le voit présent, ne le ré-ajoute pas, l'exclut du seuil) ;
 *  - la gratuité est appliquée par la Function discount-pdd (match par variantId), pas ici.
 *
 * Calcul « remisé » : on somme `final_line_price` (prix de ligne APRÈS remises) des
 * lignes NON cadeaux. C'est l'équivalent panier du sous-total remisé du checkout —
 * dans la limite de ce que Shopify expose sur le panier (cf. README).
 */

(function () {
  'use strict';

  // ===========================================================================
  // Logique pure (miroir de logic.ts) — euros
  // ===========================================================================

  var AUTO_GIFT_PROPERTY = '_zax_auto_gift';

  // "gid://shopify/ProductVariant/123" -> "123"
  function parseGid(gid) {
    if (typeof gid !== 'string') return null;
    var parts = gid.split('/');
    return parts[parts.length - 1] || null;
  }

  // Valide/normalise un arbre de conditions (miroir de logic.ts). undefined si vide.
  function normalizeConditions(raw) {
    var all = raw && raw.all;
    if (!Array.isArray(all)) return undefined;
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var c = all[i];
      if (!c || typeof c !== 'object') continue;
      var connector = c.connector === 'or' ? 'or' : 'and';
      if (c.type === 'in_cart' || c.type === 'eligibility') {
        var ids = Array.isArray(c.ids)
          ? c.ids.filter(function (x) { return typeof x === 'string' && x.length > 0; })
          : [];
        if (ids.length === 0) continue;
        var target = c.target === 'products' ? 'products' : 'collections';
        if (c.type === 'eligibility') {
          out.push({ type: 'eligibility', mode: c.mode === 'exclude' ? 'exclude' : 'include', target: target, ids: ids, connector: connector });
        } else {
          out.push({ type: 'in_cart', target: target, ids: ids, connector: connector });
        }
      } else if (c.type === 'purchase_min' || c.type === 'purchase_max') {
        if (typeof c.value !== 'number' || !(c.value >= 0)) continue;
        out.push({
          type: c.type,
          metric: c.metric === 'quantity' ? 'quantity' : 'subtotal',
          value: c.value,
          appliesTo: c.appliesTo === 'eligible' ? 'eligible' : 'cart',
          connector: connector,
        });
      }
    }
    return out.length > 0 ? { all: out } : undefined;
  }

  // Une règle est valable si elle porte un arbre `conditions` OU un `threshold` >= 0.
  function normalizeRules(rules) {
    if (!Array.isArray(rules)) return [];
    var out = [];
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (!r || typeof r.variantId !== 'string' || r.variantId.length === 0) continue;
      var conditions = normalizeConditions(r.conditions);
      var hasThreshold = typeof r.threshold === 'number' && r.threshold >= 0;
      if (!conditions && !hasThreshold) continue;
      out.push({
        variantId: r.variantId,
        threshold: hasThreshold ? r.threshold : undefined,
        // `quantityMode` = 'per_trigger_item' : une unité offerte par article déclencheur,
        // `quantity` n'étant alors qu'un plafond MAX facultatif (undefined = illimité).
        // Mode figé par défaut : `quantity` unités, 1 si non renseigné.
        quantityMode: r.quantityMode === 'per_trigger_item' ? 'per_trigger_item' : 'fixed',
        quantity: typeof r.quantity === 'number' && r.quantity > 0
          ? r.quantity
          : (r.quantityMode === 'per_trigger_item' ? undefined : 1),
        autoAdd: r.autoAdd === false ? false : true,
        giftGroup:
          !conditions && typeof r.giftGroup === 'string' && r.giftGroup.trim() !== ''
            ? r.giftGroup.trim()
            : undefined,
        conditions: conditions,
        // Prix fixe (euros) de l'offre. Ignoré par la décision toAdd/toRemove — la
        // gratuité/remise reste appliquée par discount-pdd. Sert uniquement à
        // l'AFFICHAGE hors panier (cf. computeEligibleOffers).
        fixedPrice: typeof r.fixedPrice === 'number' && r.fixedPrice >= 0 ? r.fixedPrice : undefined,
        discountId: r.discountId,
        startsAt: typeof r.startsAt === 'string' && r.startsAt !== '' ? r.startsAt : undefined,
        endsAt: typeof r.endsAt === 'string' && r.endsAt !== '' ? r.endsAt : undefined,
      });
    }
    return out;
  }

  // La règle est-elle dans sa fenêtre de validité à l'instant `now` ? (bornes ISO 8601)
  // Miroir de isWithinWindow() de logic.ts.
  function isWithinWindow(rule, now) {
    var t = now.getTime();
    if (rule.startsAt) {
      var start = Date.parse(rule.startsAt);
      if (isNaN(start) || t < start) return false;
    }
    if (rule.endsAt) {
      var end = Date.parse(rule.endsAt);
      if (isNaN(end) || t >= end) return false;
    }
    return true;
  }

  function lineMatchesTarget(line, target, idsSet) {
    if (target === 'products') return line.productId != null && !!idsSet[line.productId];
    return (line.collectionIds || []).some(function (c) { return !!idsSet[c]; });
  }

  // Conditions sur collections : vérifiables seulement si on a pu résoudre
  // l'appartenance (collectionsResolved). Sinon échec sûr (cf. computeAutoGiftChanges).
  function conditionsFullyVerifiable(conditions, collectionsResolved) {
    if (collectionsResolved) return true;
    return !conditions.all.some(function (c) {
      return (c.type === 'in_cart' || c.type === 'eligibility') && c.target === 'collections';
    });
  }

  // "gid://shopify/Collection/123" -> "123"
  function gidNum(gid) { return String(gid).split('/').pop(); }

  // GIDs de collections référencés par les conditions des règles.
  function ruleCollectionGids(rules) {
    var set = {};
    rules.forEach(function (r) {
      ((r.conditions && r.conditions.all) || []).forEach(function (c) {
        if ((c.type === 'in_cart' || c.type === 'eligibility') && c.target === 'collections') {
          (c.ids || []).forEach(function (id) { set[id] = true; });
        }
      });
    });
    return Object.keys(set);
  }

  function evaluateOneCondition(c, nonGiftLines, eligible) {
    if (c.type === 'in_cart') {
      var idsSet = {};
      c.ids.forEach(function (id) { idsSet[id] = true; });
      return nonGiftLines.some(function (l) { return lineMatchesTarget(l, c.target, idsSet); });
    }
    if (c.type === 'eligibility') return eligible.length > 0;
    var scope = c.appliesTo === 'eligible' ? eligible : nonGiftLines;
    var total = c.metric === 'quantity'
      ? scope.reduce(function (s, l) { return s + l.quantity; }, 0)
      : scope.reduce(function (s, l) { return s + l.discountedTotal; }, 0);
    return c.type === 'purchase_max' ? total <= c.value : total >= c.value;
  }

  function evaluateConditions(conditions, nonGiftLines) {
    var eligible = nonGiftLines.slice();
    conditions.all.forEach(function (c) {
      if (c.type !== 'eligibility') return;
      var idsSet = {};
      c.ids.forEach(function (id) { idsSet[id] = true; });
      eligible = eligible.filter(function (l) {
        var matches = lineMatchesTarget(l, c.target, idsSet);
        return c.mode === 'exclude' ? !matches : matches;
      });
    });
    var acc = null;
    conditions.all.forEach(function (c) {
      var value = evaluateOneCondition(c, nonGiftLines, eligible);
      if (acc === null) acc = value;
      else acc = c.connector === 'or' ? (acc || value) : (acc && value);
    });
    return acc == null ? false : acc;
  }

  // Unités « déclencheuses » du panier : somme des quantités des lignes non-cadeaux
  // satisfaisant au moins une condition de PÉRIMÈTRE (`in_cart` / `eligibility`). Une
  // ligne ciblée par deux conditions n'est comptée qu'une fois.
  // null = la règle n'a aucune condition de périmètre → il n'y a rien à compter.
  function countTriggerUnits(conditions, nonGiftLines) {
    var scoped = conditions.all.filter(function (c) {
      return c.type === 'in_cart' || c.type === 'eligibility';
    });
    if (scoped.length === 0) return null;

    var sets = scoped.map(function (c) {
      var idsSet = {};
      c.ids.forEach(function (id) { idsSet[id] = true; });
      return { condition: c, idsSet: idsSet };
    });

    var units = 0;
    nonGiftLines.forEach(function (line) {
      var matches = sets.some(function (entry) {
        var m = lineMatchesTarget(line, entry.condition.target, entry.idsSet);
        return entry.condition.mode === 'exclude' ? !m : m;
      });
      if (matches) units += line.quantity;
    });
    return units;
  }

  // Nombre d'unités cadeau que le panier DOIT contenir pour cette règle (déjà jugée
  // éligible). Proportionnel aux articles déclencheurs si `quantityMode` le demande et
  // qu'il y a de quoi compter ; sinon plafond figé (1 par défaut).
  function desiredGiftQuantity(rule, nonGiftLines) {
    if (rule.quantityMode === 'per_trigger_item' && rule.conditions) {
      var triggers = countTriggerUnits(rule.conditions, nonGiftLines);
      if (triggers !== null) {
        return rule.quantity ? Math.min(triggers, rule.quantity) : triggers;
      }
    }
    return rule.quantity || 1;
  }

  // Sous-total réel (remisé) global + par gift_group, en excluant les variants
  // cadeaux (cibles de règles) et les lignes auto-gift.
  function computeRealSubtotals(lines, giftVariantIds) {
    var global = 0;
    var byGroup = {};
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      // Exclure les cadeaux (les nôtres et toute cible de règle).
      if (line.isAutoGift || giftVariantIds[line.merchandiseId]) continue;
      global += line.discountedTotal;
      if (line.giftGroup) {
        byGroup[line.giftGroup] = (byGroup[line.giftGroup] || 0) + line.discountedTotal;
      }
    }
    return { global: global, byGroup: byGroup };
  }

  // Décision : { toAdd, toRemove } — système indépendant (seul à gérer ces cadeaux).
  // opts.collectionsResolved : true si l'appartenance aux collections a pu être résolue.
  // opts.now : Date d'évaluation de la fenêtre de validité (défaut : maintenant).
  function computeAutoGiftChanges(lines, rawRules, opts) {
    var collectionsResolved = !!(opts && opts.collectionsResolved);
    var now = opts && opts.now instanceof Date ? opts.now : new Date();
    var rules = normalizeRules(rawRules);
    var giftVariantIds = {};
    rules.forEach(function (r) {
      giftVariantIds[r.variantId] = true;
    });

    var subs = computeRealSubtotals(lines, giftVariantIds);

    // Lignes NON-cadeaux (pour l'évaluateur de conditions) — mêmes exclusions que le sous-total.
    var nonGiftLines = lines.filter(function (l) {
      return !l.isAutoGift && !giftVariantIds[l.merchandiseId];
    });

    var toAdd = [];
    var toRemove = [];
    // Ajustements de quantité d'une ligne cadeau DÉJÀ présente (mode proportionnel :
    // le nombre d'unités offertes suit le nombre d'articles déclencheurs, à la hausse
    // comme à la baisse).
    var toAdjust = [];

    // Index de NOS lignes cadeaux par variant.
    var autoGiftLinesByVariant = {};
    lines.forEach(function (line) {
      if (line.isAutoGift) {
        if (!autoGiftLinesByVariant[line.merchandiseId]) autoGiftLinesByVariant[line.merchandiseId] = [];
        autoGiftLinesByVariant[line.merchandiseId].push(line);
      }
    });

    var ruledVariants = {};

    rules.forEach(function (rule) {
      // Mode « offre auto-appliquée » (autoAdd:false) : ni ajout ni retrait — le client
      // ajoute lui-même le variant et discount-pdd remise s'il est éligible. Non marqué
      // « réglé » → le filet ci-dessous nettoie un éventuel cadeau auto résiduel (sans
      // jamais toucher une ligne ajoutée par le client, non marquée).
      if (rule.autoAdd === false) return;
      ruledVariants[rule.variantId] = true;
      var ourLines = autoGiftLinesByVariant[rule.variantId] || [];

      // meets : true (rempli) / false (non rempli) / null (indéterminé). Les conditions
      // priment sur le threshold (legacy). null = on ne sait pas évaluer (collections non
      // résolues côté thème) → on n'ajoute ni ne retire (échec sûr).
      // Hors fenêtre de validité → meets=false (on retire le cadeau si présent), prioritaire
      // sur tout le reste (pas de null : on sait que la règle est inactive maintenant).
      var meets;
      if (!isWithinWindow(rule, now)) {
        meets = false;
      } else if (rule.conditions) {
        meets = conditionsFullyVerifiable(rule.conditions, collectionsResolved)
          ? evaluateConditions(rule.conditions, nonGiftLines)
          : null;
      } else {
        var scoped = rule.giftGroup ? subs.byGroup[rule.giftGroup] || 0 : subs.global;
        meets = scoped >= rule.threshold;
      }

      if (meets === true) {
        var desired = desiredGiftQuantity(rule, nonGiftLines);

        if (desired <= 0) {
          // Éligible mais plus aucun article déclencheur à couvrir → on retire le cadeau.
          ourLines.forEach(function (line) {
            toRemove.push({ lineId: line.id, quantity: line.quantity });
          });
        } else if (ourLines.length === 0) {
          toAdd.push({ variantId: rule.variantId, quantity: desired, discountId: rule.discountId });
        } else if (rule.quantityMode === 'per_trigger_item') {
          // On aligne NOTRE première ligne sur la quantité voulue et on retire les
          // doublons éventuels. En mode figé, une ligne déjà posée n'est pas retouchée
          // (comportement historique : le client reste libre d'en ajuster la quantité).
          if (ourLines[0].quantity !== desired) {
            toAdjust.push({ lineId: ourLines[0].id, variantId: rule.variantId, quantity: desired });
          }
          ourLines.slice(1).forEach(function (line) {
            toRemove.push({ lineId: line.id, quantity: line.quantity });
          });
        }
      } else if (meets === false) {
        ourLines.forEach(function (line) {
          toRemove.push({ lineId: line.id, quantity: line.quantity });
        });
      }
    });

    // Filet de sécurité : ligne auto-gift (NÔTRE) dont le variant n'est couvert par
    // aucune règle → on la retire (règle supprimée/désactivée).
    Object.keys(autoGiftLinesByVariant).forEach(function (variantId) {
      if (ruledVariants[variantId]) return; // déjà traité dans la boucle des règles
      autoGiftLinesByVariant[variantId].forEach(function (line) {
        toRemove.push({ lineId: line.id, quantity: line.quantity });
      });
    });

    return { toAdd: toAdd, toRemove: toRemove, toAdjust: toAdjust };
  }

  // Offres de PRIX éligibles : règles portant un `fixedPrice` dont les conditions
  // sont satisfaites par le panier courant. Sert uniquement à l'AFFICHAGE hors
  // panier (fiche produit, cartes) — la remise réelle reste appliquée par
  // discount-pdd. Mêmes exclusions que computeAutoGiftChanges, pour que le prix
  // annoncé colle exactement à ce qui sera facturé.
  //
  // Contrairement à la décision d'ajout/retrait, `autoAdd` est ignoré ici : que le
  // variant soit poussé automatiquement au panier ou ajouté par le client, son prix
  // remisé est le même.
  function computeEligibleOffers(lines, rawRules, opts) {
    var collectionsResolved = !!(opts && opts.collectionsResolved);
    var now = opts && opts.now instanceof Date ? opts.now : new Date();
    var rules = normalizeRules(rawRules);

    var giftVariantIds = {};
    rules.forEach(function (r) {
      giftVariantIds[r.variantId] = true;
    });

    var subs = computeRealSubtotals(lines, giftVariantIds);
    var nonGiftLines = lines.filter(function (l) {
      return !l.isAutoGift && !giftVariantIds[l.merchandiseId];
    });

    var offers = [];
    rules.forEach(function (rule) {
      if (typeof rule.fixedPrice !== 'number') return;
      if (!isWithinWindow(rule, now)) return;

      // null (conditions non vérifiables) traité comme non éligible : on n'annonce
      // jamais un prix qu'on ne sait pas garantir.
      var meets;
      if (rule.conditions) {
        meets = conditionsFullyVerifiable(rule.conditions, collectionsResolved)
          ? evaluateConditions(rule.conditions, nonGiftLines)
          : false;
      } else {
        var scoped = rule.giftGroup ? subs.byGroup[rule.giftGroup] || 0 : subs.global;
        meets = scoped >= rule.threshold;
      }

      if (meets === true) offers.push({ variantId: rule.variantId, fixedPrice: rule.fixedPrice });
    });

    return offers;
  }

  // ===========================================================================
  // Intégration thème
  // ===========================================================================

  // Garde anti-réentrance partagée + abonnement unique à cart:update.
  // autoGiftPending : un cart:update survenu pendant une salve → on rejoue après
  // (sinon on raterait l'état final quand plusieurs mutations s'enchaînent).
  var autoGiftBusy = false;
  var autoGiftPending = false;
  // Cibles d'ajustement déjà tentées dans cette page (variantId → quantité demandée).
  // Voir le garde anti-boucle dans runAutoGiftSync.
  var attemptedAdjustments = {};
  var listenerAttached = false;

  function ajaxConfig(body) {
    // Accept: application/javascript → Shopify répond en JSON (sinon /cart/add
    // renvoie la page HTML du panier, que les autres intercepteurs n'arrivent pas
    // à parser). Aligné sur fetchConfig('javascript') du thème.
    return {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    };
  }

  function parseJSON(value, fallback, log) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (e) {
      if (log) log('JSON invalide', e);
      return fallback;
    }
  }

  async function postCart(url, body, errLabel) {
    try {
      // L'httpInterceptor du thème capte cet appel et rafraîchit le drawer.
      await fetch(url, ajaxConfig(body));
    } catch (e) {
      console.error('[AUTO-GIFTS][cart] ' + errLabel, e);
    }
  }

  // ---- Résolution des collections (token-free, via endpoints Ajax) ----------
  // product.collections (Liquid) est vide dans ce thème → on passe par
  // /collections.json (id->handle) puis /collections/<handle>/products.json (membres).
  var collectionHandleById = null; // { "<numericId>": "<handle>" }
  var collectionProductsByHandle = {}; // { "<handle>": { "<numericProductId>": true } }

  async function loadCollectionHandles() {
    if (collectionHandleById) return collectionHandleById;
    var map = {};
    for (var page = 1; page <= 20; page++) {
      var res = await fetch('/collections.json?limit=250&page=' + page, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('collections.json HTTP ' + res.status);
      var cols = (await res.json()).collections || [];
      cols.forEach(function (c) { map[String(c.id)] = c.handle; });
      if (cols.length < 250) break;
    }
    collectionHandleById = map;
    return map;
  }

  async function loadCollectionProducts(handle) {
    if (handle in collectionProductsByHandle) return collectionProductsByHandle[handle];
    var set = {};
    for (var page = 1; page <= 40; page++) {
      var res = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('products.json HTTP ' + res.status);
      var prods = (await res.json()).products || [];
      prods.forEach(function (p) { set[String(p.id)] = true; });
      if (prods.length < 250) break;
    }
    collectionProductsByHandle[handle] = set;
    return set;
  }

  // Renvoie { ok, byVariant } : map variantGid -> [collection gids des règles] auxquelles
  // le produit appartient. ok=false si une collection référencée n'a pas pu être résolue
  // (→ échec sûr sur les conditions collections).
  async function resolveCartCollections(items, colGids, log) {
    var ok = true;
    var setByGid = {};
    try {
      var handleById = await loadCollectionHandles();
      await Promise.all(colGids.map(async function (gid) {
        var handle = handleById[gidNum(gid)];
        if (!handle) { ok = false; if (log) log('handle introuvable (collection non publiée ?)', gid); return; }
        try {
          setByGid[gid] = await loadCollectionProducts(handle);
        } catch (e) { ok = false; if (log) log('produits collection KO', handle, e); }
      }));
    } catch (e) {
      if (log) log('collections.json KO', e);
      return { ok: false, byVariant: {} };
    }
    var byVariant = {};
    items.forEach(function (i) {
      var vgid = 'gid://shopify/ProductVariant/' + i.variant_id;
      var cols = [];
      colGids.forEach(function (gid) {
        if (setByGid[gid] && setByGid[gid][String(i.product_id)]) cols.push(gid);
      });
      byVariant[vgid] = cols;
    });
    return { ok: ok, byVariant: byVariant };
  }

  // Construit les lignes (collections résolues incluses) puis en dérive la décision
  // d'ajout/retrait ET les offres de prix éligibles, à partir d'un état de panier
  // DONNÉ. Appelé sur un panier fraîchement relu juste avant d'agir.
  async function computeStateFromCart(cart, rules, giftGroups, log) {
    var collectionsResolved = false;
    var collectionsByVariant = {};
    var colGids = ruleCollectionGids(normalizeRules(rules));
    if (colGids.length > 0) {
      var resolved = await resolveCartCollections(cart.items || [], colGids, log);
      collectionsResolved = resolved.ok;
      collectionsByVariant = resolved.byVariant;
      log('collections résolues:', collectionsResolved, collectionsByVariant);
    }
    var lines = (cart.items || []).map(function (item) {
      var gid = 'gid://shopify/ProductVariant/' + item.variant_id;
      var props = item.properties || {};
      return {
        id: item.key,
        quantity: item.quantity,
        merchandiseId: gid,
        productId: item.product_id != null ? 'gid://shopify/Product/' + item.product_id : null,
        collectionIds: collectionsByVariant[gid] || [],
        giftGroup: giftGroups[gid] || null,
        discountedTotal: (item.final_line_price || 0) / 100, // cents -> euros
        isAutoGift: Object.prototype.hasOwnProperty.call(props, AUTO_GIFT_PROPERTY),
      };
    });
    var opts = { collectionsResolved: collectionsResolved };
    return {
      decision: computeAutoGiftChanges(lines, rules, opts),
      offers: computeEligibleOffers(lines, rules, opts),
    };
  }

  // Publie les offres de prix pour les modules d'affichage (fiche produit, cartes).
  // `window.AutoGiftOffers` porte l'état courant — un module chargé après le premier
  // calcul le lit au démarrage plutôt que d'attendre le prochain cart:update.
  //
  // Publié à CHAQUE sync, même quand la liste est inchangée : entre deux calculs
  // identiques, un tiers a pu réécrire l'affichage du prix (variantSelects.reset()
  // appelé par sidecart après chaque mutation du panier, remises Regios). Dédupliquer
  // ici privait le module d'affichage de l'occasion de reprendre la main. Le coût est
  // nul : il compare avant d'écrire.
  function publishOffers(offers) {
    window.AutoGiftOffers = offers;
    document.body.dispatchEvent(new CustomEvent('auto-gift:offers', { detail: { offers: offers } }));
  }

  async function runAutoGiftSync(el) {
    if (!el) el = document.querySelector('auto-gift-rules');
    if (!el) return;
    // Une salve déjà en cours : on note qu'il faudra rejouer (état final).
    if (autoGiftBusy) { autoGiftPending = true; return; }

    var debug = el.dataset.debug === 'true';
    var log = function () {
      if (debug) console.log.apply(console, ['[AUTO-GIFTS][cart]'].concat([].slice.call(arguments)));
    };

    var rules = parseJSON(el.dataset.autoGiftRules, [], log);
    if (!Array.isArray(rules) || rules.length === 0) return;
    var giftGroups = parseJSON(el.dataset.giftGroups, {}, log);

    autoGiftBusy = true;
    try {
      // Décision sur le panier FRAÎCHEMENT relu, juste avant d'agir : les clés de
      // ligne sont à jour → pas de 400 sur clé périmée (retraits robustes).
      var state = await computeStateFromCart(await cartManager.getCart(), rules, giftGroups, log);
      var decision = state.decision;
      log('décision:', decision, '— offres:', state.offers);

      // Avant le court-circuit ci-dessous : l'affichage doit être rafraîchi même
      // quand le panier n'a rien à ajouter/retirer (cas d'un seuil franchi par une
      // ligne non cadeau → le prix annoncé change sans que le panier bouge).
      publishOffers(state.offers);

      // Garde anti-boucle : un ajustement peut être REFUSÉ ou ÉCRÊTÉ par Shopify (stock du
      // cadeau insuffisant, limite par commande…), et `postCart` n'en saurait rien. L'écart
      // persisterait alors, or chaque appel /cart/change.js est capté par l'httpInterceptor
      // du thème qui redispatche `cart:update` → re-sync → même ajustement → boucle. On ne
      // réémet donc jamais deux fois la même cible (variant → quantité) : si l'ajustement a
      // abouti l'écart a disparu de lui-même, s'il a échoué on n'insiste pas.
      var toAdjust = (decision.toAdjust || []).filter(function (gift) {
        return attemptedAdjustments[gift.variantId] !== gift.quantity;
      });

      if (decision.toRemove.length === 0 && decision.toAdd.length === 0 && toAdjust.length === 0) return;

      // Retraits des NÔTRES (clés fraîches).
      for (var i = 0; i < decision.toRemove.length; i++) {
        log('retrait ligne', decision.toRemove[i].lineId);
        await postCart(window.routes.cart_change_url, { id: decision.toRemove[i].lineId, quantity: 0 }, 'échec retrait');
      }
      // Ajustements de quantité des NÔTRES (mode proportionnel).
      for (var k = 0; k < toAdjust.length; k++) {
        log('ajustement ligne', toAdjust[k].lineId, '→ x' + toAdjust[k].quantity);
        attemptedAdjustments[toAdjust[k].variantId] = toAdjust[k].quantity;
        await postCart(window.routes.cart_change_url, { id: toAdjust[k].lineId, quantity: toAdjust[k].quantity }, 'échec ajustement');
      }
      // Ajouts.
      for (var j = 0; j < decision.toAdd.length; j++) {
        var gift = decision.toAdd[j];
        var id = parseGid(gift.variantId);
        if (!id) continue;
        var props = {};
        props[AUTO_GIFT_PROPERTY] = gift.discountId || '1';
        log('ajout cadeau', id, 'x' + (gift.quantity || 1));
        await postCart(window.routes.cart_add_url, { items: [{ id: id, quantity: gift.quantity || 1, properties: props }] }, 'échec ajout');
      }
    } finally {
      autoGiftBusy = false;
      // Un cart:update est survenu pendant la salve → rejouer une fois sur l'état final.
      if (autoGiftPending) { autoGiftPending = false; runAutoGiftSync(); }
    }
  }

  // Debounce : on coalesce les cart:update rapprochés (plusieurs events peuvent
  // suivre une seule action) avant de recalculer/agir une fois sur l'état stabilisé.
  var SYNC_DEBOUNCE_MS = 300;
  var syncTimer = null;
  function scheduleSync() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      syncTimer = null;
      runAutoGiftSync().catch(function (e) {
        console.error('[AUTO-GIFTS][cart]', e);
      });
    }, SYNC_DEBOUNCE_MS);
  }

  function ensureCartListener() {
    if (listenerAttached) return;
    listenerAttached = true;
    // sidecart.js dispatche cart:update sur document.body à chaque changement de panier.
    document.body.addEventListener('cart:update', scheduleSync);
  }

  customElements.define(
    'auto-gift-rules',
    class AutoGiftRules extends HTMLElement {
      connectedCallback() {
        ensureCartListener();
        scheduleSync();
      }
    },
  );

  // Exposé pour d'éventuels tests / debug manuel.
  window.AutoGiftCart = {
    computeAutoGiftChanges: computeAutoGiftChanges,
    computeEligibleOffers: computeEligibleOffers,
    normalizeRules: normalizeRules,
  };
})();
